import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import { RequisitionStatus } from '@models/matter';
import { MatterRequisitionPrecedent } from '@models/requisitions';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { NewRequisitionModalComponent } from './modals/matter-requisition/new-requisition-modal/new-requisition-modal.component';
import { NewTeranetRequisitionModalComponent } from './modals/new-teranet-requisition-modal/new-teranet-requisition-modal.component';
import {
	IUpdateMatterRequisitionModalData,
	UpdateRequisitionModalComponent,
} from './modals/matter-requisition/update-requisition-modal/update-requisition-modal.component';
import { DEFAULT_REQUISITION_DIMENSIONS } from './modals';
import { MatterRequisitionsDispatch } from '@state/matter/requisitions/matter-requisitions.dispatch';
import { MatterRequisitionsState } from '@state/matter/requisitions/matter-requisitions.state';
import { Observable } from 'rxjs';
import { Select } from '@ngxs/store';
import { MatterWordDocumentsDefaultsSource } from '@state/matter/matter-word-documents/matter-word-documents-defaults-source.selectors';
import { TestHooks } from './requisitions.hooks';

enum NewRequisitionOptions {
	ADD_NEW = 'ADD_NEW',
	TERANET_IMPORT = 'TERANET IMPORT',
}

const Items: DropdownOption<NewRequisitionOptions>[] = [
	{ name: 'Add New Requisition', value: NewRequisitionOptions.ADD_NEW },
	{
		name: 'Import From Teranet Search',
		value: NewRequisitionOptions.TERANET_IMPORT,
	},
];

@UntilDestroy()
@Component({
	selector: 'app-purchase-requisitions',
	templateUrl: './requisitions.component.html',
	animations: [openClose(300)],
	standalone: false,
})
export class PurchaseRequisitionsComponent {
	testHooks = TestHooks;
	public dropdownItems = Items;
	public ICON = ICON;

	@Select(SelectedMatter.useHtmlDocuments)
	public useHtmlDocuments$: Observable<boolean>;
	@SelectSnapshot(SelectedMatter.id) public matterId: string;
	@SelectSnapshot(MatterRequisitionsState.getAll)
	public requisitions: MatterRequisitionPrecedent[];
	@ViewSelectSnapshot(SelectedMatter.requisitionStatus)
	requisitionStatus: RequisitionStatus;
	@ViewSelectSnapshot(SelectedMatter.titleSearchCompletionDate)
	titleSearchCompletionDate: string;
	@Select(MatterWordDocumentsDefaultsSource.getRequisitionsSourceIdByMatterType)
	public requisitionsDocSourceIdByMatterType$: Observable<string>;

	public showDetails = true;

	constructor(
		private dialog: MatDialog,
		private requisitionDispatch: MatterRequisitionsDispatch,
		private matterDispatch: MatterDispatch,
	) {}

	onSelect(value: DropdownResult<DropdownOption<NewRequisitionOptions>>) {
		switch (value.result.value) {
			case NewRequisitionOptions.ADD_NEW:
				this.dialog.open(
					NewRequisitionModalComponent,
					DEFAULT_REQUISITION_DIMENSIONS,
				);
				break;
			case NewRequisitionOptions.TERANET_IMPORT:
				this.dialog.open(
					NewTeranetRequisitionModalComponent,
					DEFAULT_REQUISITION_DIMENSIONS,
				);
				break;
			default:
				throw new Error('Invalid option');
		}
	}

	onDelete(requisitionId: string) {
		this.requisitionDispatch.Remove(requisitionId);
	}

	updateRequisition(selectedRequisitionId: string) {
		this.dialog.open<
			UpdateRequisitionModalComponent,
			IUpdateMatterRequisitionModalData
		>(UpdateRequisitionModalComponent, {
			...DEFAULT_REQUISITION_DIMENSIONS,
			data: { selectedRequisitionId },
		});
	}

	drop(dropEvent: CdkDragDrop<MatterRequisitionPrecedent>) {
		const previousRequisition = this.requisitions[dropEvent.currentIndex];
		const currentRequisition = dropEvent.item.data;
		if (
			dropEvent.previousIndex !== dropEvent.currentIndex &&
			previousRequisition &&
			currentRequisition
		) {
			this.requisitionDispatch.Update(previousRequisition.id, {
				order: dropEvent.previousIndex,
			});
			this.requisitionDispatch.Update(currentRequisition.id, {
				order: dropEvent.currentIndex,
			});
		}
	}

	setRequisitionUploaded(val: boolean) {
		this.matterDispatch.SetRequisitionStatus(this.matterId, { uploaded: val });
	}

	setRequisitionLetterDate(date: string) {
		this.matterDispatch.SetRequisitionStatus(this.matterId, {
			requisitionLetterDate: date,
		});
	}

	setRequisitionDate(date: string) {
		this.matterDispatch.Update(this.matterId, {
			titleSearchCompletionDate: date,
		});
	}

	toggleDetails() {
		this.showDetails = !this.showDetails;
	}

	trackByRequisitionId(
		_index: number,
		requisition: MatterRequisitionPrecedent,
	) {
		return requisition.id;
	}
}
